import {
  Engine, Scene, FreeCamera, MeshBuilder, StandardMaterial, HemisphericLight, DirectionalLight,
  Vector3, Color3, Color4, TransformNode, type Mesh,
} from "@babylonjs/core";
import {
  PLAYER_HEIGHT, PLAYER_RADIUS, EYE_HEIGHT, KIND_COLORS, KINDS, type BoxDef, type MapDef,
} from "@fragout/shared";

const PLAYER_COLORS = ["#5EE22E", "#FF3DBE", "#1ED0EE", "#FFC81E", "#FF3B3B", "#9B5CFF", "#1EE0B0", "#FF7A1A"];

interface Boom { mesh: Mesh; ring: Mesh; t: number; life: number; size: number }

export class Renderer {
  engine: Engine;
  scene: Scene;
  camera: FreeCamera;
  private players = new Map<string, Mesh>();
  private grenades = new Map<number | string, Mesh>();
  private knives = new Map<string, Mesh>();
  private drops = new Map<string, Mesh>();
  private pads = new Map<string, { base: Mesh; item: Mesh }>();
  private mats = new Map<string, StandardMaterial>();
  private booms: Boom[] = [];
  private handRoot: TransformNode;
  private handNade: Mesh;
  private handKnife: Mesh;
  private arc: import("@babylonjs/core").LinesMesh | undefined;
  private arcDot: Mesh | undefined;
  private spin = 0;

  constructor(canvas: HTMLCanvasElement, map: MapDef) {
    this.engine = new Engine(canvas, true, { preserveDrawingBuffer: false, stencil: false }, true);
    this.scene = new Scene(this.engine);
    // The game reads its own mouse. Cancelling pointerdown here would suppress the browser's
    // compatibility mousedown, which is what starved the input layer of every click.
    this.scene.preventDefaultOnPointerDown = false;
    this.scene.preventDefaultOnPointerUp = false;
    this.scene.clearColor = new Color4(0.75, 0.89, 1.0, 1);
    this.scene.ambientColor = new Color3(0.6, 0.6, 0.65);
    this.camera = new FreeCamera("cam", new Vector3(0, EYE_HEIGHT, -10), this.scene);
    this.camera.minZ = 0.05;
    this.camera.maxZ = 400;
    this.camera.fov = 1.2;
    this.camera.inputs.clear();
    const hemi = new HemisphericLight("hemi", new Vector3(0.2, 1, 0.1), this.scene);
    hemi.intensity = 0.75;
    hemi.groundColor = new Color3(0.55, 0.55, 0.6);
    const sun = new DirectionalLight("sun", new Vector3(-0.4, -1, 0.3), this.scene);
    sun.intensity = 0.9;
    for (const b of map.boxes) this.addBox(b);
    for (const pd of map.pads) this.addPad(pd.id, pd.kind, pd.x, pd.y, pd.z);

    // First person hand: a grenade or the knife floating bottom right of the view.
    this.handRoot = new TransformNode("hand", this.scene);
    this.handRoot.parent = this.camera;
    this.handRoot.position.set(0.42, -0.32, 0.9);
    this.handNade = MeshBuilder.CreateSphere("handNade", { diameter: 0.22, segments: 10 }, this.scene);
    this.handNade.parent = this.handRoot;
    this.handNade.material = this.material(KIND_COLORS[1]);
    this.handKnife = MeshBuilder.CreateBox("handKnife", { width: 0.03, height: 0.05, depth: 0.42 }, this.scene);
    this.handKnife.parent = this.handRoot;
    this.handKnife.rotation.x = -0.4;
    this.handKnife.material = this.material("#E8ECF5");
    this.handKnife.setEnabled(false);

    window.addEventListener("resize", () => this.engine.resize());
  }

  private material(hex: string, emissive = 0): StandardMaterial {
    const key = hex + ":" + emissive;
    let m = this.mats.get(key);
    if (!m) {
      m = new StandardMaterial("m" + key, this.scene);
      const c = Color3.FromHexString(hex);
      m.diffuseColor = c;
      m.specularColor = Color3.Black();
      m.ambientColor = c.scale(0.5);
      if (emissive > 0) m.emissiveColor = c.scale(emissive);
      this.mats.set(key, m);
    }
    return m;
  }

  private addBox(b: BoxDef) {
    const mesh = MeshBuilder.CreateBox("box", { width: b.hx * 2, height: b.hy * 2, depth: b.hz * 2 }, this.scene);
    mesh.position.set(b.x, b.y, b.z);
    mesh.rotation.set(b.rx ?? 0, b.ry ?? 0, b.rz ?? 0);
    mesh.material = this.material(b.color);
    mesh.enableEdgesRendering();
    mesh.edgesWidth = 2.5;
    mesh.edgesColor = new Color4(0.08, 0.09, 0.12, 1);
    mesh.freezeWorldMatrix();
  }

  private addPad(id: string, kind: number, x: number, y: number, z: number) {
    const base = MeshBuilder.CreateCylinder("pad" + id, { diameter: 1.6, height: 0.08, tessellation: 24 }, this.scene);
    base.position.set(x, y + 0.04, z);
    base.material = this.material(kind ? KIND_COLORS[kind] : "#9AA0B4", kind ? 0.25 : 0);
    const item = MeshBuilder.CreateSphere("item" + id, { diameter: kind === 5 ? 0.5 : 0.3, segments: 10 }, this.scene);
    item.position.set(x, y + 0.9, z);
    item.material = this.material(kind ? KIND_COLORS[kind] : "#9AA0B4", 0.6);
    item.setEnabled(false);
    this.pads.set(id, { base, item });
  }

  setPad(id: string, active: boolean) {
    const p = this.pads.get(id);
    if (!p) return;
    p.item.setEnabled(active);
  }

  playerMesh(id: string, colour: number): Mesh {
    let m = this.players.get(id);
    if (!m) {
      m = MeshBuilder.CreateCapsule("p" + id, { height: PLAYER_HEIGHT, radius: PLAYER_RADIUS, tessellation: 12, subdivisions: 1 }, this.scene);
      m.material = this.material(PLAYER_COLORS[colour % PLAYER_COLORS.length]);
      const visor = MeshBuilder.CreateBox("v" + id, { width: 0.5, height: 0.12, depth: 0.1 }, this.scene);
      visor.material = this.material("#14161F");
      visor.parent = m;
      visor.position.set(0, PLAYER_HEIGHT * 0.32, PLAYER_RADIUS);
      this.players.set(id, m);
    }
    return m;
  }

  /** Feet position and yaw for another player's body. */
  placePlayer(id: string, colour: number, x: number, y: number, z: number, yaw: number, crouch: boolean) {
    const m = this.playerMesh(id, colour);
    m.position.set(x, y + PLAYER_HEIGHT / 2 * (crouch ? 0.7 : 1), z);
    m.scaling.y = crouch ? 0.7 : 1;
    m.rotation.y = yaw;
    m.setEnabled(true);
  }

  hidePlayer(id: string) { const m = this.players.get(id); if (m) m.setEnabled(false); }
  removePlayer(id: string) { const m = this.players.get(id); if (m) { m.dispose(false, true); this.players.delete(id); } }

  placeGrenade(id: number | string, kind: number, x: number, y: number, z: number, hot: boolean) {
    let m = this.grenades.get(id);
    if (!m) {
      const def = KINDS[kind] ?? KINDS[1];
      m = MeshBuilder.CreateSphere("g" + id, { diameter: def.radius * 2 * 1.6, segments: 8 }, this.scene);
      m.material = this.material(KIND_COLORS[kind] ?? KIND_COLORS[1], 0.7);
      this.grenades.set(id, m);
    }
    m.position.set(x, y, z);
    m.scaling.setAll(hot ? 1.35 : 1);
    m.setEnabled(true);
  }

  /** Remove grenade meshes not in `keep`. */
  pruneGrenades(keep: Set<number | string>) {
    for (const [id, m] of this.grenades) if (!keep.has(id)) { m.dispose(false, true); this.grenades.delete(id); }
  }

  placeKnife(id: string, x: number, y: number, z: number, vx: number, vy: number, vz: number, stuck: boolean) {
    let m = this.knives.get(id);
    if (!m) {
      m = MeshBuilder.CreateBox("k" + id, { width: 0.05, height: 0.08, depth: 0.5 }, this.scene);
      m.material = this.material("#E8ECF5", 0.2);
      this.knives.set(id, m);
    }
    m.position.set(x, y, z);
    if (!stuck && (vx || vz)) { m.rotation.y = Math.atan2(vx, vz); m.rotation.x = -Math.atan2(vy, Math.hypot(vx, vz)); }
    m.setEnabled(true);
  }

  pruneKnives(keep: Set<string>) {
    for (const [id, m] of this.knives) if (!keep.has(id)) { m.dispose(false, true); this.knives.delete(id); }
  }

  placeDrop(id: string, kind: number, x: number, y: number, z: number) {
    let m = this.drops.get(id);
    if (!m) {
      m = MeshBuilder.CreateBox("d" + id, { size: 0.35 }, this.scene);
      m.material = this.material(KIND_COLORS[kind] ?? "#ffffff", 0.5);
      this.drops.set(id, m);
    }
    m.position.set(x, y + 0.35, z);
    m.rotation.y = this.spin;
    m.setEnabled(true);
  }

  pruneDrops(keep: Set<string>) {
    for (const [id, m] of this.drops) if (!keep.has(id)) { m.dispose(false, true); this.drops.delete(id); }
  }

  boom(x: number, y: number, z: number, kind: number) {
    const def = KINDS[kind] ?? KINDS[1];
    const size = def.falloffR;
    const mesh = MeshBuilder.CreateSphere("boom", { diameter: 1, segments: 10 }, this.scene);
    mesh.position.set(x, y, z);
    const mat = new StandardMaterial("boomMat", this.scene);
    mat.diffuseColor = Color3.FromHexString("#FF7A1A");
    mat.emissiveColor = Color3.FromHexString("#FFC81E");
    mat.alpha = 0.85;
    mesh.material = mat;
    const ring = MeshBuilder.CreateTorus("ring", { diameter: 1, thickness: 0.12, tessellation: 24 }, this.scene);
    ring.position.set(x, Math.max(0.05, y - 0.2), z);
    const rmat = new StandardMaterial("ringMat", this.scene);
    rmat.emissiveColor = Color3.FromHexString("#14161F");
    rmat.diffuseColor = Color3.Black();
    rmat.alpha = 0.9;
    ring.material = rmat;
    this.booms.push({ mesh, ring, t: 0, life: 0.35, size });
  }

  /** Per frame housekeeping: explosions, spinning pickups. */
  tick(dt: number) {
    this.spin += dt * 2;
    for (const p of this.pads.values()) { p.item.rotation.y = this.spin; p.item.position.y += Math.sin(this.spin * 2) * 0.002; }
    for (let i = this.booms.length - 1; i >= 0; i--) {
      const b = this.booms[i];
      b.t += dt;
      const k = Math.min(1, b.t / b.life);
      const s = b.size * (0.3 + 0.7 * Math.sqrt(k)) * 2;
      b.mesh.scaling.setAll(s * 0.5);
      (b.mesh.material as StandardMaterial).alpha = 0.85 * (1 - k);
      b.ring.scaling.set(s * 1.4, 1, s * 1.4);
      (b.ring.material as StandardMaterial).alpha = 0.9 * (1 - k);
      if (k >= 1) { b.mesh.material?.dispose(); b.mesh.dispose(); b.ring.material?.dispose(); b.ring.dispose(); this.booms.splice(i, 1); }
    }
  }

  /** First person camera at the eyes. Own body is not drawn. */
  /** The aiming arc, up to the first bounce. Pass null to hide it. */
  setArc(points: { x: number; y: number; z: number }[] | null) {
    if (!points || points.length < 2) {
      if (this.arc) this.arc.setEnabled(false);
      return;
    }
    const pts = points.map((p) => new Vector3(p.x, p.y, p.z));
    this.arc = MeshBuilder.CreateLines("arc", { points: pts, updatable: true, instance: this.arc ?? undefined }, this.scene);
    this.arc.color = Color3.FromHexString("#14161F");
    this.arc.alpha = 0.5;
    this.arc.isPickable = false;
    this.arc.setEnabled(true);
    // The last point is where it lands, so mark it.
    const end = points[points.length - 1];
    if (!this.arcDot) {
      this.arcDot = MeshBuilder.CreateSphere("arcdot", { diameter: 0.22, segments: 6 }, this.scene);
      this.arcDot.material = this.material("#14161F", 0.15);
      this.arcDot.isPickable = false;
    }
    this.arcDot.position.set(end.x, end.y, end.z);
    this.arcDot.setEnabled(true);
  }

  private hideArc() {
    if (this.arc) this.arc.setEnabled(false);
    if (this.arcDot) this.arcDot.setEnabled(false);
  }

  placeCamera(x: number, y: number, z: number, yaw: number, pitch: number, crouch: boolean) {
    const eye = crouch ? EYE_HEIGHT * 0.65 : EYE_HEIGHT;
    this.camera.position.set(x, y + eye, z);
    this.camera.rotation.set(-pitch, yaw, 0);
  }

  /** Death view: pulled back and up from where you fell, looking at the spot. */
  placeDeathCamera(x: number, y: number, z: number, yaw: number, t: number) {
    const back = Math.min(1, t * 2) * 4;
    const cx = x - Math.sin(yaw) * back, cz = z - Math.cos(yaw) * back;
    this.camera.position.set(cx, y + 2.2 + back * 0.4, cz);
    this.camera.setTarget(new Vector3(x, y + 1, z));
  }

  /** What the hand holds: a grenade of `kind` (glowing while cooking) or the knife. */
  setHand(kind: number, cooking: boolean, knife: boolean, knifeOut: boolean) {
    this.handKnife.setEnabled(knife && !knifeOut);
    this.handNade.setEnabled(!knife && kind > 0);
    if (!knife && kind > 0) {
      this.handNade.material = this.material(KIND_COLORS[kind] ?? KIND_COLORS[1], cooking ? 1.2 : 0.3);
      this.handNade.scaling.setAll(kind === 5 ? 1.8 : 1);
    }
    this.handRoot.position.y = -0.32 + (cooking ? 0.06 : 0);
  }

  render() { this.scene.render(); }
}
